import React from 'react';
import { ChallengeItem } from '../schemas/challenge';
import {
  Item,
  ItemMedia,
  ItemContent,
  ItemTitle,
  ItemDescription,
  ItemActions,
  ItemGroup,
} from '@repo/ui/Item';

export interface ChallengeSelectorProps {
  challenges: ChallengeItem[];
  onSelect: (type: string) => void;
  loading?: boolean;
}

const challengeLabels: Record<string, { label: string; description: string; icon: string }> = {
  // Uppercase versions
  TOTP: {
    label: 'Authenticator App',
    description: 'Use your authenticator app to generate a code',
    icon: 'smartphone',
  },
  AUTHENTICATOR_TOTP: {
    label: 'Authenticator App',
    description: 'Use your authenticator app to generate a code',
    icon: 'smartphone',
  },
  EMAIL_TOTP: {
    label: 'Email Code',
    description: 'Receive a code via email',
    icon: 'mail',
  },
  SMS_TOTP: {
    label: 'SMS Code',
    description: 'Receive a code via text message',
    icon: 'sms',
  },
  BACKUP_TOKEN: {
    label: 'Backup Code',
    description: 'Use one of your backup codes',
    icon: 'key',
  },
};

function getChallengeLabel(type: string) {
  // Try exact match first
  if (challengeLabels[type]) {
    return challengeLabels[type];
  }
  // Try uppercase
  const upper = type.toUpperCase();
  if (challengeLabels[upper]) {
    return challengeLabels[upper];
  }
  // Default fallback
  return {
    label: type,
    description: `Verify using ${type}`,
    icon: 'lock',
  };
}

export function ChallengeSelector({ challenges, onSelect, loading }: ChallengeSelectorProps) {
  // Filter out Z_TOKEN (handled automatically) and sort so BACKUP_TOKEN is always last
  const visibleChallenges = challenges.filter((c) => c.type.toUpperCase() !== 'Z_TOKEN');
  const sortedChallenges = [...visibleChallenges].sort((a, b) => {
    const aIsBackup = a.type.toUpperCase() === 'BACKUP_TOKEN';
    const bIsBackup = b.type.toUpperCase() === 'BACKUP_TOKEN';
    if (aIsBackup && !bIsBackup) return 1;
    if (!aIsBackup && bIsBackup) return -1;
    return 0;
  });

  return (
    <div className="flex flex-col gap-3 w-full">
      <p className="mb-2 text-center text-grey-600 dark:text-grey-300">
        Select a verification method to continue
      </p>
      <ItemGroup className="flex flex-col gap-3">
        {sortedChallenges.map((challenge) => {
          const { label, description, icon } = getChallengeLabel(challenge.type);
          return (
            <Item key={challenge.type} asChild>
              <button
                type="button"
                disabled={loading}
                onClick={() => onSelect(challenge.type)}
                className="text-left transition-colors cursor-pointer hover:bg-grey-100 dark:hover:bg-blue-900 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ItemMedia className="flex justify-center items-center">
                  <span className="text-2xl text-blue-600 material-symbols-outlined dark:text-blue-400">
                    {icon}
                  </span>
                </ItemMedia>
                <ItemContent>
                  <ItemTitle>{label}</ItemTitle>
                  <ItemDescription>{description}</ItemDescription>
                </ItemContent>
                <ItemActions>
                  <span className="material-symbols-outlined text-grey-400">chevron_right</span>
                </ItemActions>
              </button>
            </Item>
          );
        })}
      </ItemGroup>
    </div>
  );
}
